import React, { useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useSite } from '../context/SiteContext';
import { PublicFooter } from '../components/PublicWebsite';
import { useLoginLanguage } from '../components/LoginLanguage';
import { LoginHeader, LoginIntroduction } from '../components/login/LoginPresentation';
import { LoginForm } from '../components/login/LoginForm';
import { LoginLegal } from '../components/login/LoginHelp';
import { AndroidDownload } from '../components/AndroidDownload';
import { customerAppLogin } from '../lib/androidApp';
import { Button } from '../components/ui/button';
import { safePaymentReturn, afterPaymentLogin } from '../lib/paymentReturn';
import './LoginPage.css';
import ForgotPasswordPage from './ForgotPasswordPage';

export default function LoginPage() {
  const [credentials, setCredentials] = useState({ identifier: new URLSearchParams(window.location.search).get('username') || '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const query = new URLSearchParams(window.location.search);
  const [recovery, setRecovery] = useState(query.get('mode') === 'recovery');
  const inFlight = useRef(false);
  const { user, loading: authLoading, loginUnified, loginCustomerRecovery, loginCustomerEither, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSite();
  const language = useLoginLanguage();
  const { text } = language;
  const customerOnly = customerAppLogin();
  const returnTo = safePaymentReturn(new URLSearchParams(window.location.search).get('next'));
  const toggleRecovery = () => { setRecovery(value => !value); setError(''); setShowPw(false); setCredentials(previous => ({ ...previous, password: '' })); };

  if (query.get('forgot') === '1') return <ForgotPasswordPage />;
  if (!authLoading && user && (!customerOnly || user.kind === 'customer')) return <Navigate to={afterPaymentLogin(user, returnTo)} replace />;

  const submit = async (e) => {
    e.preventDefault(); if (inFlight.current) return;
    inFlight.current = true; setLoading(true); setError('');
    const method = recovery ? loginCustomerRecovery : customerOnly ? loginCustomerEither : loginUnified;
    const res = await method(credentials.identifier.trim(), credentials.password);
    inFlight.current = false; setLoading(false); setCredentials(previous => ({ ...previous, password: '' }));
    if (res.ok) { toast.success(text.welcome); navigate(afterPaymentLogin(res.user, returnTo), { replace: true }); }
    else setError(res.error);
  };

  return (
    <div className="login-scene" data-testid="login-page">
      <img className="login-backdrop" src="/maldhowr-login-lobby.webp" alt="" aria-hidden="true" fetchPriority="high" />
      <div className="login-container">
        <LoginHeader language={language} />
        {settings?.frontend?.announcement && <div role="status" data-testid="website-announcement" className="login-notice">{settings.frontend.announcement}</div>}
        {settings?.maintenance?.enabled && <div role="status" data-testid="login-maintenance-notice" className="login-notice login-notice-maintenance">{settings.maintenance.message}</div>}
        <main className="login-main">
          <LoginIntroduction text={text} />
          <section className="login-panel" aria-labelledby="login-title" data-testid="login-panel">
            <img data-testid="login-gold-mark" src="/maldhowr-mark.png" alt="MALDHOWR emblem" className="login-mark" />
            <h2 id="login-title" data-testid="login-title" className="login-title">{text.signIn}</h2>
            <p data-testid="login-subtitle" className="login-subtitle">{recovery ? 'Use your existing password to set up or reset your customer PIN.' : 'Welcome back to MALDHOWR.'}</p>
            {query.get('pinReady') === '1' && <p data-testid="login-pin-ready" className="login-notice" role="status">Your PIN is ready. Use it to sign in and approve payments.</p>}
            {query.get('passwordReset') === '1' && <p data-testid="login-password-reset-success" className="login-notice" role="status">Password changed successfully. Sign in with your new password.</p>}
            {customerOnly && user && user.kind !== 'customer' ? <div data-testid="customer-app-role-notice" className="space-y-3 text-sm"><p>This app is for customer accounts.</p><Button data-testid="customer-app-switch-account" onClick={logout}>Sign out and use a customer account</Button></div> : <LoginForm text={text} unifiedMode={!recovery} showGoogle={!customerOnly && !recovery} showPw={showPw} togglePassword={() => setShowPw(v => !v)} loading={loading} error={error}
              identifier={credentials.identifier} password={credentials.password}
              onIdentifier={identifier => setCredentials(v => ({ ...v, identifier }))}
              onPassword={password => setCredentials(v => ({ ...v, password }))} onSubmit={submit} onGoogle={loginWithGoogle} />}
            {!recovery && <p data-testid="login-customer-pin-guidance" className="text-xs text-slate-600 text-center mt-4">Customers can sign in with their password or four-digit PIN.</p>}
            <button data-testid="login-pin-recovery-toggle" type="button" className="login-pin-recovery-toggle" disabled={loading} onClick={toggleRecovery}>{recovery ? 'Back to sign in' : 'Set up or reset customer PIN'}</button>
            <AndroidDownload />
            <LoginLegal text={text} />
          </section>
        </main>
        <PublicFooter />
      </div>
    </div>
  );
}